/**
 * The question author (#29): create and manage a reusable pool of questions that boards
 * draw from. A question is a `question`-kind content item in the account's bank pack; the
 * clue editor's "Insert from question bank" copies one onto a board. Difficulty is 1–5;
 * tags live on the item row.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MAX_DIFFICULTY, MIN_DIFFICULTY } from '@gsf/games';
import { bank, type PoolQuestion } from '@games/jeopardy/ui/content';
import { Toast } from '../kit/Toast';
import { AuthGate } from '../components/AuthGate';

export function Questions() {
  return (
    <AuthGate title="Question bank">
      <QuestionsInner />
    </AuthGate>
  );
}

const DIFFICULTIES = Array.from(
  { length: MAX_DIFFICULTY - MIN_DIFFICULTY + 1 },
  (_, i) => MIN_DIFFICULTY + i,
);

const PAGE_SIZE = 50;

function QuestionsInner() {
  const [questions, setQuestions] = useState<PoolQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState(0); // 0 = any
  const [editing, setEditing] = useState<PoolQuestion | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // "Submit New Question" from the home page lands here with ?new=1; open the editor once,
  // then drop the param so a refresh does not reopen it.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('new') === null) return;
    setEditing('new');
    params.delete('new');
    setParams(params, { replace: true });
  }, [params, setParams]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setQuestions(await bank.listQuestions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your questions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const q of questions) for (const t of q.tags) set.add(t);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [questions]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return questions.filter(
      (item) =>
        (q === '' ||
          item.prompt.toLowerCase().includes(q) ||
          item.correctAnswer.toLowerCase().includes(q)) &&
        (tagFilter === '' || item.tags.includes(tagFilter)) &&
        (difficultyFilter === 0 || item.difficulty === difficultyFilter),
    );
  }, [questions, query, tagFilter, difficultyFilter]);

  // Paginate: a library of thousands must not render as one list.
  const [page, setPage] = useState(0);
  // Any filter change (or a shrinking list) returns to the first page.
  useEffect(() => {
    setPage(0);
  }, [query, tagFilter, difficultyFilter]);
  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const visible = shown.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);

  async function remove(item: PoolQuestion) {
    if (!confirm(`Delete this question?\n\n${item.prompt || '(no prompt)'}`)) return;
    try {
      await bank.deletePoolQuestion(item.id);
      setQuestions((cur) => cur.filter((q) => q.id !== item.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that question.');
    }
  }

  return (
    <div className="admin">
      <div className="admin-header">
        <h1>Question bank</h1>
        <span className="spacer" />
        <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          New question
        </button>
        <Link className="btn btn-sm" to="/">
          Home
        </Link>
      </div>

      <p className="muted" style={{ marginTop: 0 }}>
        Questions you author here are reusable: pick them from a board with “Search existing
        clues” in the clue editor.
      </p>

      <div className="row" style={{ flexWrap: 'wrap', gap: '.5rem', alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '2 1 14rem' }}>
          <label htmlFor="q-search">Search</label>
          <input
            id="q-search"
            placeholder="Prompt or answer…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: '1 1 10rem' }}>
          <label htmlFor="q-tag">Tag</label>
          <select id="q-tag" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">Any tag</option>
            {allTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 8rem' }}>
          <label htmlFor="q-diff">Difficulty</label>
          <select
            id="q-diff"
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(Number(e.target.value))}
          >
            <option value={0}>Any</option>
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : questions.length === 0 ? (
        <p className="muted">
          No questions yet. Select <strong>New question</strong> to add your first.
        </p>
      ) : shown.length === 0 ? (
        <p className="muted">No questions match those filters.</p>
      ) : (
        <>
        <ul className="game-list">
          {visible.map((item) => (
            <li key={item.id}>
              <span className="col" style={{ gap: '.15rem', minWidth: 0 }}>
                <strong>{item.prompt || '(no prompt)'}</strong>
                <span className="faint">
                  {item.correctAnswer || '(no answer)'}
                  {item.incorrectAnswers.length > 0
                    ? ` · ${item.incorrectAnswers.length} distractor${item.incorrectAnswers.length === 1 ? '' : 's'}`
                    : ''}
                  {` · difficulty ${item.difficulty}`}
                </span>
                {item.tags.length > 0 ? (
                  <span className="row" style={{ flexWrap: 'wrap', gap: '.25rem' }}>
                    {item.tags.map((t) => (
                      <span key={t} className="chip">
                        {t}
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
              <span className="spacer" />
              <button className="btn btn-sm btn-ghost" onClick={() => setEditing(item)}>
                Edit
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => void remove(item)}>
                Delete
              </button>
            </li>
          ))}
        </ul>
        <div className="row" style={{ alignItems: 'center', marginTop: '.5rem' }}>
          <span className="faint">
            {shown.length} question{shown.length === 1 ? '' : 's'}
            {pageCount > 1 ? ` · page ${clampedPage + 1} of ${pageCount}` : ''}
          </span>
          {pageCount > 1 ? (
            <>
              <span className="spacer" />
              <button
                className="btn btn-sm btn-ghost"
                disabled={clampedPage === 0}
                onClick={() => setPage(clampedPage - 1)}
              >
                ‹ Prev
              </button>
              <button
                className="btn btn-sm btn-ghost"
                disabled={clampedPage >= pageCount - 1}
                onClick={() => setPage(clampedPage + 1)}
              >
                Next ›
              </button>
            </>
          ) : null}
        </div>
        </>
      )}

      {editing ? (
        <QuestionEditor
          question={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved, created) => {
            setQuestions((cur) =>
              created ? [saved, ...cur] : cur.map((q) => (q.id === saved.id ? saved : q)),
            );
            setEditing(null);
            setNotice(created ? 'Question added.' : 'Saved.');
          }}
          onError={setError}
        />
      ) : null}

      <Toast message={error} onDismiss={() => setError(null)} />
      <Toast message={notice} onDismiss={() => setNotice(null)} />
    </div>
  );
}

function QuestionEditor({
  question,
  onClose,
  onSaved,
  onError,
}: {
  question: PoolQuestion | null;
  onClose: () => void;
  onSaved: (saved: PoolQuestion, created: boolean) => void;
  onError: (message: string) => void;
}) {
  const [prompt, setPrompt] = useState(question?.prompt ?? '');
  const [correctAnswer, setCorrectAnswer] = useState(question?.correctAnswer ?? '');
  const [incorrectAnswers, setIncorrectAnswers] = useState<string[]>(
    question?.incorrectAnswers.length ? [...question.incorrectAnswers] : [''],
  );
  const [difficulty, setDifficulty] = useState(question?.difficulty ?? MIN_DIFFICULTY);
  const [tags, setTags] = useState((question?.tags ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function setDistractor(index: number, text: string) {
    setIncorrectAnswers((cur) => cur.map((a, i) => (i === index ? text : a)));
  }

  async function save() {
    if (prompt.trim() === '') {
      setProblem('Add a prompt.');
      return;
    }
    if (correctAnswer.trim() === '') {
      setProblem('Add a correct answer.');
      return;
    }
    setSaving(true);
    setProblem(null);
    const input = {
      prompt,
      correctAnswer,
      incorrectAnswers,
      difficulty,
      tags: tags.split(','),
    };
    try {
      const saved =
        question === null
          ? await bank.createPoolQuestion(input)
          : await bank.updatePoolQuestion(question.id, input);
      onSaved(saved, question === null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save that question.';
      setProblem(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-wide"
        role="dialog"
        aria-label="Question editor"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-head">
          <h2 className="panel-title" style={{ margin: 0 }}>
            {question === null ? 'New question' : 'Edit question'}
          </h2>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        {problem ? <p className="error-text">{problem}</p> : null}

        <div className="col">
          <div className="field">
            <label htmlFor="qe-prompt">Prompt</label>
            <textarea
              id="qe-prompt"
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="qe-answer">Correct answer</label>
            <input id="qe-answer" value={correctAnswer} onChange={(e) => setCorrectAnswer(e.target.value)} />
          </div>

          <div className="field">
            <label>Incorrect answers</label>
            <p className="faint" style={{ margin: '0 0 .4rem' }}>
              Optional. Used as distractors when a board imports this as multiple choice.
            </p>
            {incorrectAnswers.map((a, i) => (
              <div className="row" key={i} style={{ alignItems: 'center' }}>
                <input
                  style={{ flex: 1 }}
                  value={a}
                  placeholder="Wrong answer"
                  onChange={(e) => setDistractor(i, e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  aria-label="Remove answer"
                  onClick={() => setIncorrectAnswers((cur) => cur.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setIncorrectAnswers((cur) => [...cur, ''])}
            >
              Add another
            </button>
          </div>

          <div className="field">
            <label>Difficulty</label>
            <div className="seg-group" role="group" aria-label="Difficulty">
              {DIFFICULTIES.map((d) => (
                <button
                  type="button"
                  key={d}
                  className={`btn btn-sm${difficulty === d ? ' btn-primary' : ' btn-ghost'}`}
                  aria-pressed={difficulty === d}
                  onClick={() => setDifficulty(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label htmlFor="qe-tags">Tags</label>
            <input
              id="qe-tags"
              value={tags}
              placeholder="history, science, 1990s"
              onChange={(e) => setTags(e.target.value)}
            />
            <span className="faint">Comma-separated.</span>
          </div>

          <div className="row" style={{ marginTop: '.5rem' }}>
            <span className="spacer" />
            <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save question'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
